/**
 * LongevityDiagram — Animated SVG showing factors that affect system lifespan.
 * Bars grow in with CSS animation to draw attention during explanation.
 */

export function LongevityDiagram() {
  const factors = [
    { label: 'Water treatment', impact: 90, positive: true },
    { label: 'Annual servicing', impact: 85, positive: true },
    { label: 'Low cycling rate', impact: 75, positive: true },
    { label: 'Quality install', impact: 80, positive: true },
    { label: 'Sludge buildup', impact: -70, positive: false },
    { label: 'Short-cycling', impact: -65, positive: false },
    { label: 'Hard water scale', impact: -50, positive: false },
    { label: 'Poor maintenance', impact: -80, positive: false },
  ];

  const positiveFactors = factors.filter(f => f.positive);
  const negativeFactors = factors.filter(f => !f.positive);

  const barHeight = 22;
  const gap = 6;
  const maxWidth = 140;
  const centerX = 240;
  const startY = 50;

  return (
    <figure style={{ margin: 0 }}>
      <svg
        viewBox="0 0 480 300"
        width="100%"
        role="img"
        aria-label="Factors affecting system longevity"
      >
        <title>Positive and negative factors affecting heating system lifespan</title>
        <defs>
          <style>{`
            .ld-label { font-family: system-ui, sans-serif; font-size: 11px; fill: #475569; }
            .ld-heading { font-family: system-ui, sans-serif; font-size: 13px; fill: #1e293b; font-weight: 600; text-anchor: middle; }
            .ld-bar-pos { fill: #22c55e; opacity: 0.8; rx: 3; animation: ld-grow-left 0.8s ease-out both; }
            .ld-bar-neg { fill: #ef4444; opacity: 0.8; rx: 3; animation: ld-grow-right 0.8s ease-out both; }
            .ld-center { stroke: #475569; stroke-width: 1.5; }
            .ld-subtitle { font-family: system-ui, sans-serif; font-size: 10px; fill: #94a3b8; text-anchor: middle; }
            .ld-pulse { animation: ld-pulse 2.5s ease-in-out infinite; }
            @keyframes ld-grow-left {
              from { transform: scaleX(0); }
              to { transform: scaleX(1); }
            }
            @keyframes ld-grow-right {
              from { transform: scaleX(0); }
              to { transform: scaleX(1); }
            }
            @keyframes ld-pulse {
              0%, 100% { opacity: 0.7; }
              50% { opacity: 1; }
            }
          `}</style>
        </defs>

        <text x="240" y="25" className="ld-heading">What extends or shortens system life</text>

        {/* Column headers */}
        <text x="170" y="45" className="ld-label" textAnchor="end" style={{ fill: '#22c55e', fontWeight: 600 }}>
          Extends life
        </text>
        <text x="310" y="45" className="ld-label" textAnchor="start" style={{ fill: '#ef4444', fontWeight: 600 }}>
          Shortens life
        </text>

        {/* Center line */}
        <line x1={centerX} y1={startY} x2={centerX} y2={startY + 8 * (barHeight + gap)} className="ld-center" />

        {/* Positive bars (left side) — animated grow */}
        {positiveFactors.map((f, i) => {
          const y = startY + i * (barHeight + gap);
          const barW = (f.impact / 100) * maxWidth;
          return (
            <g key={f.label}>
              <rect
                x={centerX - barW}
                y={y}
                width={barW}
                height={barHeight}
                className="ld-bar-pos ld-pulse"
                style={{ transformOrigin: `${centerX}px ${y + barHeight / 2}px`, animationDelay: `${i * 0.15}s` }}
              />
              <text
                x={centerX - barW - 5}
                y={y + barHeight / 2 + 4}
                className="ld-label"
                textAnchor="end"
              >
                {f.label}
              </text>
            </g>
          );
        })}

        {/* Negative bars (right side) — animated grow */}
        {negativeFactors.map((f, i) => {
          const y = startY + i * (barHeight + gap);
          const barW = (Math.abs(f.impact) / 100) * maxWidth;
          return (
            <g key={f.label}>
              <rect
                x={centerX}
                y={y}
                width={barW}
                height={barHeight}
                className="ld-bar-neg ld-pulse"
                style={{ transformOrigin: `${centerX}px ${y + barHeight / 2}px`, animationDelay: `${(i + 4) * 0.15}s` }}
              />
              <text
                x={centerX + barW + 5}
                y={y + barHeight / 2 + 4}
                className="ld-label"
                textAnchor="start"
              >
                {f.label}
              </text>
            </g>
          );
        })}

        {/* Lifespan range */}
        <text x="240" y={startY + 8 * (barHeight + gap) + 20} className="ld-subtitle">
          Well-maintained system: 15–20 years · Poorly maintained: 7–10 years
        </text>
      </svg>
    </figure>
  );
}
